import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';
import MarkdownIt from 'markdown-it';
import isPrivateOrInternalHost from './UrlRiskDetector';

const setImageHeight = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  const url = new URL(imgSrc);
  const height = url.searchParams.get('cw_image_height');
  if (!height) return;
  inlineToken.attrSet('style', `height: ${height};`);
};

const processInlineToken = blockToken => {
  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') {
      setImageHeight(inlineToken);
    }
  });
};

const imgResizeManager = md => {
  // Custom rule for image resize in markdown
  // If the image url has a query param cw_image_height, then add a style attribute to the image
  md.core.ruler.after('inline', 'add-image-height', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') {
        processInlineToken(blockToken);
      }
    });
  });
};

const flagRiskyLink = inlineToken => {
  const href = inlineToken.attrGet('href');
  if (!href) return;

  let hostname;
  try {
    hostname = new URL(href).hostname;
  } catch (error) {
    return;
  }

  if (!isPrivateOrInternalHost(hostname)) return;

  // Só marca o link — o tooltip de verdade (mesmo v-tooltip/floating-vue
  // usado no resto do app) é anexado depois, no FormattedContent.vue, via
  // createTooltip() da própria lib. Não dá pra usar a diretiva v-tooltip
  // direto no template porque esse HTML é injetado via v-dompurify-html,
  // fora da árvore de componentes Vue — mas createTooltip() é a mesma
  // função que a diretiva chama por baixo dos panos, então o resultado é
  // idêntico a qualquer outro tooltip do sistema, sem CSS/HTML duplicado.
  inlineToken.attrSet('data-risky-link', 'true');
};

const flagRiskyLinks = md => {
  // Marca links cujo host seja um IP/hostname privado ou interno
  // (ex: 10.x.x.x, 192.168.x.x, localhost) pra alerta visual. `mila`
  // (markdown-it-link-attributes) sobrescreve `class`/`rel`/`target` em
  // tempo de render sem fazer merge — por isso usamos `data-risky-link`,
  // atributo que ela não toca.
  md.core.ruler.after('inline', 'flag-risky-links', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type !== 'inline') return;
      blockToken.children.forEach(inlineToken => {
        if (inlineToken.type === 'link_open') {
          flagRiskyLink(inlineToken);
        }
      });
    });
  });
};

const createMarkdownInstance = (linkify = true) => {
  return MarkdownIt({
    html: false,
    xhtmlOut: true,
    breaks: true,
    langPrefix: 'language-',
    linkify,
    typographer: true,
    quotes: '\u201c\u201d\u2018\u2019',
    maxNesting: 20,
  })
    .use(mentionPlugin)
    .use(imgResizeManager)
    .use(flagRiskyLinks)
    .use(mila, {
      attrs: {
        class: 'link',
        rel: 'noreferrer noopener nofollow',
        target: '_blank',
      },
    });
};

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';

class MessageFormatter {
  constructor(
    message,
    isATweet = false,
    isAPrivateNote = false,
    linkify = true
  ) {
    this.message = message || '';
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.linkify = linkify;
    this.md = createMarkdownInstance(linkify);
  }

  formatMessage() {
    let updatedMessage = this.message;
    if (this.isATweet && !this.isAPrivateNote) {
      updatedMessage = updatedMessage.replace(
        TWITTER_USERNAME_REGEX,
        TWITTER_USERNAME_REPLACEMENT
      );
      updatedMessage = updatedMessage.replace(
        TWITTER_HASH_REGEX,
        TWITTER_HASH_REPLACEMENT
      );
    }
    return this.md.render(updatedMessage);
  }

  get formattedMessage() {
    return this.formatMessage();
  }

  get plainText() {
    const strippedOutHtml = new DOMParser().parseFromString(
      this.formattedMessage,
      'text/html'
    );
    return strippedOutHtml.body.textContent || '';
  }
}

export default MessageFormatter;
